<template>
  <div
    class="product-set"
    :class="[schema.sectionBgColor, schema.layout]"
    :style="sectionPaddingStyle"
    :id="`product-set-${schema.collection}`"
  >
    <div class="product-set__container container">
      <div class="product-set__collection-container">
        <div
          class="product-set__collection"
          :style="collectionStyle($mq)"
          :class="[horizontalLayout, verticalLayout]"
        >
          <div
            class="product-set__overlay"
            v-if="schema.enableOverlay"
            :class="schema.imageOverlayColor"
            :style="collectionOverlayStyle"
          ></div>
          <div :class="['product-set__collection-content', schema.text_align]">
            <h5
              class="product-set__collection-title"
              :class="schema.textColor"
            >{{ schema.collectionTitle }}</h5>
            <p
              class="product-set__collection-description"
              :class="schema.textColor"
            >{{ schema.collectionDescription }}</p>
            <div class="product-set__collection-cta-container">
              <v-button
                class="product-set__collection-cta"
                v-if="schema.ctaLayout.includes('button')"
                :href="schema.collectionUrl"
                :class="schema.ctaLayout"
                tag="a"
              >{{schema.ctaText}}</v-button>
              <v-link
                class="product-set__collection-cta"
                v-if="schema.ctaLayout.includes('link')"
                :href="schema.collectionUrl"
                :class="schema.ctaLayout"
              >{{schema.ctaText}}</v-link>
            </div>
          </div>
        </div>
      </div>
      <v-link
        class="product-set__section-title product-set__section-title--mobile"
        :primary="true"
        :href="schema.collectionUrl"
      >{{ schema.sectionTitle}}</v-link>
      <div class="product-set__carousel-container">
        <v-link
          class="product-set__section-title"
          :primary="true"
          :href="schema.collectionUrl"
        >{{ schema.sectionTitle}}</v-link>
        <swiper :options="swiperOptions" ref="carousel">
          <swiper-slide
            class="product-set__slide"
            v-for="product in schema.products"
            :key="product.id"
          >
            <product-tile-widget :settings="settings" :product="product"></product-tile-widget>
          </swiper-slide>
        </swiper>
        <div class="product-set__carousel-controls-container">
          <div class="product-set__carousel-control product-set__carousel-control--next">
            <v-arrow-button class="product-set__nav-button next" :primary="true">
              <icon name="chevron-right" size="16px"></icon>
            </v-arrow-button>
          </div>
          <div class="product-set__carousel-control product-set__carousel-control--prev">
            <v-arrow-button class="product-set__nav-button prev" :primary="true">
              <icon name="chevron-left" size="16px"></icon>
            </v-arrow-button>
          </div>
        </div>
        <div class="product-set__pagination"></div>
      </div>
    </div>
  </div>
</template>

<script>
import ProductTileWidget from '@/scripts/components/product/ProductTileWidget.vue';
import VButton from '@/scripts/components/buttons/VButton.vue';
import VLink from '@/scripts/components/buttons/VLink.vue';
import VArrowButton from '@/scripts/components/buttons/VArrowButton.vue';
import Icon from '@/scripts/components/basic/Icon.vue';
import { swiper, swiperSlide } from 'vue-awesome-swiper';

import { flexSwitch } from '@/scripts/helpers/flex';

import { px } from '@/scripts/filters/string.js';

export default {
  name: 'ProductSet',
  props: {
    schema: Object,
    settings: Object,
  },
  components: {
    ProductTileWidget,
    VButton,
    VLink,
    VArrowButton,
    Icon,
    swiper,
    swiperSlide,
  },
  computed: {
    collectionOverlayStyle() {
      return {
        opacity: this.schema.overlayOpacity / 100,
      };
    },
    sectionPaddingStyle() {
      const {
        schema: { topPadding, bottomPadding },
      } = this;
      return {
        padding: `${px(topPadding)} 0 ${px(bottomPadding)} 0`,
      };
    },
    verticalLayout() {
      return flexSwitch('align', this.schema.vertical_layout);
    },
    horizontalLayout() {
      return flexSwitch('justify', this.schema.horizontal_layout);
    },
    swiperOptions() {
      return {
        slidesPerView: 2,
        pagination: {
          el: `.product-set#product-set-${this.schema.collection} .product-set__pagination`,
          clickable: true,
        },
        loop: true,
        spaceBetween: 40,
        navigation: {
          nextEl: `.product-set#product-set-${this.schema.collection} .next`,
          prevEl: `.product-set#product-set-${this.schema.collection} .prev`,
        },
        breakpoints: {
          1199: {
            spaceBetween: 16,
            slidesPerView: 2,
          },
          1023: {
            spaceBetween: 16,
            slidesPerView: 4,
          },
          767: {
            spaceBetween: 16,
            slidesPerView: 3,
          },
          580: {
            spaceBetween: 16,
            slidesPerView: 2,
          },
        },
      };
    },
  },
  methods: {
    collectionStyle(mq) {
      return {
        backgroundImage: `url(${
          mq === 'mobile'
            ? this.schema.collectionImageMobile
            : this.schema.collectionImage
        })`,
      };
    },
  },
};
</script>

<style lang="scss">
.product-set {
  &__collection {
    position: relative;
    min-height: 439px;
    padding: 24px 16px;
    margin-bottom: 16px;
    background-size: cover;
  }
  &__overlay {
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
    &.dark {
      background-color: $color-black;
    }
    &.light {
      background-color: $color-white;
    }
  }
  &__nav-button {
    &.prev {
      transform: rotate(180deg);
    }
    svg {
      stroke-width: 1px;
      stroke: transparent;
      transition: stroke 150ms ease-in;
    }
    &:hover {
      svg {
        stroke: $color-black;
      }
    }
  }
  &__collection-content {
    position: relative;
  }
  &__section-title {
    &:not(.product-set__section-title--mobile) {
      text-align: center;
      margin-top: -36px;
      position: absolute;
      transform: translateY(-100%);
      @include mobile-tablet {
        display: none;
      }
    }
    &--mobile {
      display: none;
      @include mobile-tablet {
        display: block;
        margin-bottom: 16px;
      }
    }
  }
  &__collection-title {
    margin-bottom: 8px;
  }
  &__collection-description {
    margin-bottom: 24px;
  }
  &__carousel-controls-container {
    display: none;
  }
  &__carousel-container {
    position: relative;
  }
  &__carousel-control {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    &--next {
      right: 20px;
    }
    &--prev {
      left: 20px;
    }
  }
  &__pagination {
    text-align: center;
    padding-top: 16px;
    @include desktop-up {
      display: none;
    }
    .swiper-pagination-bullet {
      margin: 0 4px;
      background: $color-grey;
      &-active {
        background: $color-brand-primary;
      }
    }
  }
  @include mobile-tablet {
    .product-set {
      &__container,
      &__section-title {
        max-width: $breakpoint-tablet;
      }
    }
  }
  @include desktop-up {
    &__carousel-controls-container {
      display: block;
    }
  }
  &.half {
    @include desktop-up {
      .product-set {
        &__container {
          display: flex;
          align-items: center;
        }
        &__carousel-container,
        &__collection-container {
          flex: 1;
        }
        &__carousel-container {
          max-width: 57%;
          padding: 50px 64px 0;
          display: flex;
        }
        &__carousel-control {
          &--next {
            right: 12px;
          }
          &--prev {
            left: 12px;
          }
        }
        &__collection {
          display: flex;
          width: 100%;
          min-height: 730px;
          padding: 40px 20px;
          margin: 0;
          background-size: cover;
        }
      }
    }
    @include desktop-up {
      .product-set {
        &__carousel-container {
          padding: 0 80px;
        }
        &__carousel-control {
          &--next {
            right: 20px;
          }
          &--prev {
            left: 20px;
          }
        }
      }
    }
    @include hd-up {
      .product-set {
        &__carousel-container {
          padding: 50px 120px 0;
        }
        &__carousel-control {
          &--next {
            right: 40px;
          }
          &--prev {
            left: 40px;
          }
        }
      }
    }
  }
}
</style>
